import React, { useCallback, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";
import { useAuth } from "react-use-auth";
import { useDropzone } from "react-dropzone";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import { LayoutDefault } from "../../Layouts/LayoutDefault";
import { ContentSection } from "../../common/ContentSection/ContentSection";
import { Button } from "../../common/Button/Button";
import { ButtonType } from "../../common/Button/ButtonType";
import { Dropdown } from "../../common/Dropdown/Dropdown";
import { FileDropZone } from "../../common/FileUploader/FileDropZone";
import { ConfirmationModal } from "../../common/Modal/ConfirmationModal";
import { Modal } from "../../common/Modal/Modal";
import {
  parseZipPdfsFromFile,
  OcrProgressEvent,
} from "../../../utils/parseZipPdfs";
import {
  applyManualOverride,
  buildCompletenessReport,
  classifyUnmatchedWithLlm,
  EctdFileMatch,
  SectionRowStatus,
  matchLoosePdfsToEctd,
  needsLlmClassification,
} from "../../../utils/matchLoosePdfsToEctd";
import {
  EctdSection,
  ectdSections,
  findSectionByKey,
  sectionsForModule,
} from "../../../utils/ectd/ectdSections";
import {
  GroupedAnalysisResult,
  runGroupedEctdAnalysis,
} from "../../../utils/runGroupedEctdAnalysis";
import {
  ScannedPageMode,
  readScannedPageMode,
  writeScannedPageMode,
} from "../../../utils/aiCommon";
import { styledTheme } from "../../../theme/theme";

const STATUS_LABEL: Record<SectionRowStatus, string> = {
  present: "Present",
  missing: "Missing",
  "not-applicable": "N/A declared",
};

const STATUS_COLOR: Record<SectionRowStatus, string> = {
  present: "#1e7d32",
  missing: "#c62828",
  "not-applicable": "#757575",
};

const CONFIDENCE_COLOR: Record<string, string> = {
  high: "#1e7d32",
  medium: "#e09b00",
  low: "#c62828",
};

const baseName = (path: string): string =>
  path.replace(/\\/g, "/").split("/").pop() || path;

export const EctdMapping = (): JSX.Element => {
  const { authResult } = useAuth();
  const authToken = authResult?.accessToken as string | undefined;

  const [parsing, setParsing] = useState<boolean>(false);
  const [ocrProgress, setOcrProgress] = useState<OcrProgressEvent | null>(null);
  const [llmClassifying, setLlmClassifying] = useState<boolean>(false);
  const [uploadedName, setUploadedName] = useState<string>("");
  const [matches, setMatches] = useState<EctdFileMatch[] | null>(null);
  const [showAllModules, setShowAllModules] = useState<boolean>(false);
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [scannedPageMode, setScannedPageMode] = useState<ScannedPageMode>(() =>
    readScannedPageMode()
  );

  const [analysisRunning, setAnalysisRunning] = useState<boolean>(false);
  const [progressTotal, setProgressTotal] = useState<number>(0);
  const [progressCompleted, setProgressCompleted] = useState<number>(0);
  const [progressLastGroup, setProgressLastGroup] = useState<string>("");
  const [groupResults, setGroupResults] = useState<GroupedAnalysisResult[]>([]);
  const [isClearModalOpen, setIsClearModalOpen] = useState<boolean>(false);
  const [transcriptionMatch, setTranscriptionMatch] =
    useState<EctdFileMatch | null>(null);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const report = useMemo(
    () => (matches ? buildCompletenessReport(matches) : null),
    [matches]
  );

  const summary = useMemo(() => {
    if (!matches) return null;
    const by = (matchedBy: string): number =>
      matches.filter((m) => m.sectionCode && m.matchedBy === matchedBy).length;
    const pageTotals = matches.reduce(
      (acc, m) => {
        const stats = m.file.pageStats;
        if (!stats) return acc;
        return {
          totalPages: acc.totalPages + stats.totalPages,
          textPages: acc.textPages + stats.textPages,
          scannedPages: acc.scannedPages + stats.scannedPages,
        };
      },
      { totalPages: 0, textPages: 0, scannedPages: 0 }
    );
    return {
      total: matches.length,
      matched: matches.filter((m) => m.sectionCode).length,
      unmatched: matches.filter((m) => !m.sectionCode).length,
      header: by("header"),
      hints: by("folder") + by("filename"),
      llm: by("llm"),
      manual: by("manual"),
      ...pageTotals,
    };
  }, [matches]);

  const overrideOptions = useMemo(() => {
    const module3 = sectionsForModule(3)
      .filter((s) => s.parentKey !== null)
      .sort((a, b) =>
        a.code.localeCompare(b.code, undefined, { numeric: true })
      );
    return module3.map((s) => ({
      value: s.key,
      label: `${s.code} — ${s.title}`,
    }));
  }, []);

  const handleScannedPageModeChange = useCallback(
    (mode: ScannedPageMode): void => {
      setScannedPageMode(mode);
      writeScannedPageMode(mode);
    },
    []
  );

  const processZip = useCallback(
    async (file: File): Promise<void> => {
      setParsing(true);
      setOcrProgress(null);
      try {
        // Read the ZIP directly as an ArrayBuffer. The previous data-URL +
        // JSON.stringify round-trip overflowed the browser string limit
        // ("allocation size overflow") on large archives.
        const parsed = await parseZipPdfsFromFile(file, {
          authToken,
          scannedPageMode,
          onOcrProgress: (event) => {
            setOcrProgress(event);
          },
        });
        if (parsed.length === 0) {
          toast.error("No PDF files found in the ZIP archive.");
          return;
        }
        setUploadedName(file.name);
        const deterministic = matchLoosePdfsToEctd(parsed);
        setMatches(deterministic);

        const unresolved = deterministic.filter(needsLlmClassification);
        if (unresolved.length > 0) {
          setLlmClassifying(true);
          try {
            const refined = await classifyUnmatchedWithLlm(deterministic, {
              authToken,
            });
            setMatches(refined);
          } finally {
            setLlmClassifying(false);
          }
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        toast.error(`ZIP processing failed: ${message}`);
      } finally {
        setParsing(false);
        setOcrProgress(null);
      }
    },
    [authToken, scannedPageMode]
  );

  const uploadDisabled = parsing || analysisRunning || !!matches;

  const handleDrop = useCallback(
    (acceptedFiles: File[]): void => {
      const file = acceptedFiles[0];
      if (!file || uploadDisabled) return;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      processZip(file);
    },
    [processZip, uploadDisabled]
  );

  const { getRootProps, getInputProps, isDragAccept, open } = useDropzone({
    accept: "application/zip,.zip,application/x-zip-compressed",
    maxFiles: 1,
    onDrop: handleDrop,
    onDropRejected: (rejected): void => {
      if (rejected.length > 0) {
        toast.error("Please upload a ZIP file only.");
      }
    },
  });

  const handleOverride = useCallback(
    (fileName: string, sectionKey: string | null): void => {
      setMatches((prev) =>
        prev ? applyManualOverride(prev, fileName, sectionKey) : prev
      );
    },
    []
  );

  const handleRunAnalysis = useCallback(async (): Promise<void> => {
    if (!matches) return;
    setAnalysisRunning(true);
    setProgressTotal(0);
    setProgressCompleted(0);
    setProgressLastGroup("");
    try {
      const results = await runGroupedEctdAnalysis(
        matches,
        (completed, total, label) => {
          setProgressTotal(total);
          setProgressCompleted(completed);
          if (label) setProgressLastGroup(label);
        },
        { authToken, scannedPageMode }
      );
      setGroupResults(results);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(`Grouped analysis failed: ${message}`);
    } finally {
      setAnalysisRunning(false);
    }
  }, [matches, authToken, scannedPageMode]);

  const handleClear = useCallback((): void => {
    setMatches(null);
    setGroupResults([]);
    setUploadedName("");
    setExpandedCells(new Set());
    setProgressTotal(0);
    setProgressCompleted(0);
    setProgressLastGroup("");
    setTranscriptionMatch(null);
  }, []);

  const toggleCell = useCallback((cellKey: string): void => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(cellKey)) next.delete(cellKey);
      else next.add(cellKey);
      return next;
    });
  }, []);

  const allModuleSections = useMemo(() => {
    if (!showAllModules || !matches) return [];
    const filesBySectionKey = new Map<string, EctdFileMatch[]>();
    matches.forEach((m) => {
      if (!m.section) return;
      const list = filesBySectionKey.get(m.section.key) || [];
      list.push(m);
      filesBySectionKey.set(m.section.key, list);
    });
    const coveredCache = new Map<string, boolean>();
    const isCovered = (section: EctdSection): boolean => {
      const cached = coveredCache.get(section.key);
      if (cached !== undefined) return cached;
      const hasFiles = (filesBySectionKey.get(section.key) || []).length > 0;
      let covered = hasFiles;
      if (!covered && section.childKeys.length > 0) {
        covered = section.childKeys.every((key) => {
          const child = findSectionByKey(key);
          return child ? isCovered(child) : false;
        });
      }
      coveredCache.set(section.key, covered);
      return covered;
    };

    return ectdSections
      .map((s, index) => ({ s, index }))
      .filter(({ s }) => s.parentKey !== null)
      .sort((a, b) => a.s.module - b.s.module || a.index - b.index)
      .map(({ s }) => ({
        section: s,
        files: filesBySectionKey.get(s.key) || [],
        covered: isCovered(s),
      }));
  }, [showAllModules, matches]);

  const transcriptionCloseButton = (
    <Button
      label="Close"
      type={ButtonType.Secondary}
      onClick={(): void => setTranscriptionMatch(null)}
    />
  );

  return (
    <LayoutDefault pageTitle="eCTD mapping">
      <Container>
        <ContentSection
          title="eCTD mapping — loose PDF set"
          stepNumber={1}
          wrapCardsCallback={(): void => {}}
          buttons={[]}
          fullWidth
        >
          {/* Card 1: upload */}
          <Card>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>
              Upload a ZIP with loose Module 3 PDF files (no index.xml required)
            </h3>
            <p style={{ margin: "0 0 12px", color: "#555", fontSize: 14 }}>
              Each PDF is matched to a canonical eCTD section key using the
              section codes found on its first page, plus folder and filename
              hints. Unresolved files are classified with an LLM. Scanned pages
              are detected automatically and either transcribed or attached as
              images for analysis.
            </p>
            <ModeRow>
              <span style={{ fontSize: 13, color: "#333" }}>
                Scanned pages:
              </span>
              <ModeToggle
                type="button"
                $active={scannedPageMode === "transcribe"}
                disabled={uploadDisabled}
                onClick={(): void => handleScannedPageModeChange("transcribe")}
              >
                Transcribe to text
              </ModeToggle>
              <ModeToggle
                type="button"
                $active={scannedPageMode === "attach-images"}
                disabled={uploadDisabled}
                onClick={(): void =>
                  handleScannedPageModeChange("attach-images")
                }
              >
                Attach page images
              </ModeToggle>
            </ModeRow>
            <FileDropZone
              rootProps={getRootProps({ isDragAccept })}
              inputProps={getInputProps()}
              open={open}
              disabled={uploadDisabled}
            />
            {uploadedName && !parsing && (
              <div style={{ marginTop: 8, fontSize: 13, color: "#333" }}>
                Processed file: <strong>{uploadedName}</strong>
              </div>
            )}
            {parsing && (
              <div style={{ marginTop: 12, padding: 12 }}>
                {!ocrProgress && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 6,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>Reading ZIP…</div>
                      <div style={{ color: "#333" }}>0%</div>
                    </div>
                    <div
                      style={{
                        height: 10,
                        background: "#eee",
                        borderRadius: 6,
                        overflow: "hidden",
                      }}
                      aria-label="ZIP read progress"
                    >
                      <div
                        style={{
                          height: "100%",
                          width: "0%",
                          background: styledTheme.newColors.primary.basic,
                        }}
                      />
                    </div>
                  </div>
                )}
                {ocrProgress && ocrProgress.phase === "classify" && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 6,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>
                        Classifying PDF files
                      </div>
                      <div style={{ color: "#333" }}>
                        {ocrProgress.fileCount > 0
                          ? `${Math.floor(
                              (ocrProgress.filesCompleted /
                                ocrProgress.fileCount) *
                                100
                            )}%`
                          : "0%"}
                      </div>
                    </div>
                    <div
                      style={{
                        height: 10,
                        background: "#eee",
                        borderRadius: 6,
                        overflow: "hidden",
                      }}
                      aria-label="PDF classification progress"
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${
                            ocrProgress.fileCount > 0
                              ? (ocrProgress.filesCompleted /
                                  ocrProgress.fileCount) *
                                100
                              : 0
                          }%`,
                          background: styledTheme.newColors.primary.basic,
                          transition: "width 300ms ease",
                        }}
                      />
                    </div>
                    <div
                      style={{ marginTop: 6, fontSize: 12, color: "#555" }}
                    >
                      {`${ocrProgress.filesCompleted} of ${ocrProgress.fileCount} files classified`}
                      {ocrProgress.label ? ` · ${ocrProgress.label}` : ""}
                    </div>
                  </div>
                )}
                {ocrProgress && ocrProgress.phase === "extract" && (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 6,
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>Pages</div>
                        <div style={{ color: "#333" }}>
                          {ocrProgress.overallTotal > 0
                            ? `${Math.floor(
                                (ocrProgress.overallCompleted /
                                  ocrProgress.overallTotal) *
                                  100
                              )}%`
                            : "Counting pages…"}
                        </div>
                      </div>
                      <div
                        style={{
                          height: 10,
                          background: "#eee",
                          borderRadius: 6,
                          overflow: "hidden",
                        }}
                        aria-label="OCR pages progress"
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${
                              ocrProgress.overallTotal > 0
                                ? (ocrProgress.overallCompleted /
                                    ocrProgress.overallTotal) *
                                  100
                                : 0
                            }%`,
                            background: styledTheme.newColors.primary.basic,
                            transition: "width 300ms ease",
                          }}
                        />
                      </div>
                      <div
                        style={{ marginTop: 6, fontSize: 12, color: "#555" }}
                      >
                        {ocrProgress.overallTotal > 0
                          ? `${ocrProgress.overallCompleted} of ${ocrProgress.overallTotal} pages processed`
                          : "Counting pages…"}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 6,
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>Files</div>
                        <div style={{ color: "#333" }}>
                          {ocrProgress.fileCount > 0
                            ? `${Math.floor(
                                (ocrProgress.filesCompleted /
                                  ocrProgress.fileCount) *
                                  100
                              )}%`
                            : "0%"}
                        </div>
                      </div>
                      <div
                        style={{
                          height: 10,
                          background: "#eee",
                          borderRadius: 6,
                          overflow: "hidden",
                        }}
                        aria-label="OCR files progress"
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${
                              ocrProgress.fileCount > 0
                                ? (ocrProgress.filesCompleted /
                                    ocrProgress.fileCount) *
                                  100
                                : 0
                            }%`,
                            background: styledTheme.newColors.primary.basic,
                            transition: "width 300ms ease",
                          }}
                        />
                      </div>
                      <div
                        style={{ marginTop: 6, fontSize: 12, color: "#555" }}
                      >
                        {`${ocrProgress.filesCompleted} of ${ocrProgress.fileCount} files processed`}
                        {ocrProgress.label ? ` · ${ocrProgress.label}` : ""}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {llmClassifying && (
              <div style={{ marginTop: 8, color: "#555" }}>
                Classifying unresolved files with LLM…
              </div>
            )}
          </Card>

          {/* Card 2: summary */}
          {summary && (
            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>
                  Matching summary{uploadedName ? ` — ${uploadedName}` : ""}
                </h3>
                <Button
                  label="Clear and upload another set"
                  onClick={(): void => setIsClearModalOpen(true)}
                />
              </div>
              <ChipRow>
                <Chip>
                  <strong>{summary.total}</strong>&nbsp;PDF files
                </Chip>
                <Chip style={{ background: "#e8f5e9" }}>
                  <strong>{summary.matched}</strong>&nbsp;matched
                </Chip>
                <Chip
                  style={{
                    background: summary.unmatched ? "#ffebee" : undefined,
                  }}
                >
                  <strong>{summary.unmatched}</strong>&nbsp;unmatched
                </Chip>
                <Chip>
                  <strong>{summary.header}</strong>&nbsp;by header
                </Chip>
                <Chip>
                  <strong>{summary.hints}</strong>&nbsp;by folder/filename
                </Chip>
                <Chip>
                  <strong>{summary.llm}</strong>&nbsp;by LLM
                </Chip>
                {summary.manual > 0 && (
                  <Chip>
                    <strong>{summary.manual}</strong>&nbsp;manual
                  </Chip>
                )}
              </ChipRow>
              {summary.totalPages > 0 && (
                <ChipRow style={{ marginTop: 8 }}>
                  <Chip>
                    <strong>{summary.totalPages}</strong>&nbsp;pages
                  </Chip>
                  <Chip>
                    <strong>{summary.textPages}</strong>&nbsp;text pages
                  </Chip>
                  <Chip
                    style={{
                      background:
                        summary.scannedPages > 0 ? "#fff8e1" : undefined,
                    }}
                  >
                    <strong>{summary.scannedPages}</strong>&nbsp;scanned pages
                  </Chip>
                </ChipRow>
              )}
              {report && (
                <ChipRow style={{ marginTop: 8 }}>
                  {report.variants
                    .filter((v) => v.variantRootKey !== null)
                    .map((v) => {
                      const relevant = v.rows.filter(
                        (r) => r.status !== "not-applicable"
                      );
                      const present = relevant.filter(
                        (r) => r.status === "present"
                      ).length;
                      const pct = relevant.length
                        ? Math.round((present / relevant.length) * 100)
                        : 100;
                      return (
                        <Chip key={v.name}>
                          {v.name}:&nbsp;<strong>{pct}%</strong>&nbsp;coverage
                        </Chip>
                      );
                    })}
                </ChipRow>
              )}
            </Card>
          )}

          {/* Card 3: completeness matrix */}
          {report && (
            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>
                  eCTD completeness
                  {report.detectedModules.length > 0
                    ? ` — Module ${report.detectedModules.join(", ")} detected`
                    : ""}
                </h3>
                <Button
                  label={
                    showAllModules
                      ? "Show detected modules only"
                      : "Show all modules (M1–M5)"
                  }
                  onClick={(): void => setShowAllModules((v) => !v)}
                />
              </div>
              {report.variants.map((variant) => (
                <div key={variant.name} style={{ marginBottom: 16 }}>
                  <VariantHeader>{variant.name}</VariantHeader>
                  <MatrixTable>
                    <thead>
                      <tr>
                        <th style={{ width: 110 }}>Section</th>
                        <th>Title</th>
                        <th style={{ width: 120 }}>Status</th>
                        <th>Files</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variant.rows.map((row) => {
                        const cellKey = `${variant.name}::${row.section.key}`;
                        const expanded = expandedCells.has(cellKey);
                        const visibleFiles = expanded
                          ? row.files
                          : row.files.slice(0, 1);
                        const isGroupHeader = row.section.childKeys.length > 0;
                        return (
                          <tr
                            key={row.section.key}
                            className={
                              isGroupHeader ? "section-header" : undefined
                            }
                          >
                            <td>
                              {row.section.code}
                              {row.section.optional && (
                                <OptionalMark title="Optional in the DTD">
                                  *
                                </OptionalMark>
                              )}
                            </td>
                            <td>{row.section.title}</td>
                            <td>
                              <StatusBadge
                                style={{
                                  background: STATUS_COLOR[row.status],
                                }}
                              >
                                {row.status === "present" &&
                                row.files.length === 0
                                  ? "Covered"
                                  : STATUS_LABEL[row.status]}
                              </StatusBadge>
                            </td>
                            <td>
                              {visibleFiles.map((m) => (
                                <div
                                  key={m.file.fileName}
                                  title={m.file.fileName}
                                >
                                  {baseName(m.file.fileName)}
                                </div>
                              ))}
                              {row.files.length > 1 && (
                                <LinkButton
                                  type="button"
                                  onClick={(): void => toggleCell(cellKey)}
                                >
                                  {expanded
                                    ? "Show less"
                                    : `Show all ${row.files.length} files`}
                                </LinkButton>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </MatrixTable>
                </div>
              ))}
              <div style={{ fontSize: 12, color: "#777" }}>
                * — section is optional in the eCTD DTD. &quot;Covered&quot; —
                no file matched directly, but all content is provided in
                subsections.
              </div>
              {showAllModules && (
                <div style={{ marginTop: 16 }}>
                  <VariantHeader>Full eCTD backbone (M1–M5)</VariantHeader>
                  <MatrixTable>
                    <thead>
                      <tr>
                        <th style={{ width: 110 }}>Section</th>
                        <th>Title</th>
                        <th style={{ width: 120 }}>Matched files</th>
                        <th style={{ width: 90 }}>Covered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allModuleSections.map(({ section, files, covered }) => (
                        <tr
                          key={section.key}
                          className={
                            section.childKeys.length > 0
                              ? "section-header"
                              : undefined
                          }
                        >
                          <td style={{ paddingLeft: 8 + section.depth * 12 }}>
                            {section.code}
                          </td>
                          <td>{section.title}</td>
                          <td
                            style={{
                              color: files.length ? "#1e7d32" : "#999",
                            }}
                          >
                            {files.length || "—"}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {covered ? (
                              <CoveredTick
                                title={
                                  files.length > 0
                                    ? "Files present"
                                    : "All child sections present"
                                }
                                aria-label="Covered"
                              >
                                ✓
                              </CoveredTick>
                            ) : (
                              ""
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </MatrixTable>
                </div>
              )}
            </Card>
          )}

          {/* Card 4: file-to-key table */}
          {matches && (
            <Card>
              <h3 style={{ marginTop: 0, marginBottom: 8 }}>
                File-to-key mapping ({matches.length} files)
              </h3>
              <MatrixTable>
                <thead>
                  <tr>
                    <th>File</th>
                    <th style={{ width: 280 }}>eCTD section</th>
                    <th style={{ width: 140 }}>Variant</th>
                    <th style={{ width: 60 }}>Pages</th>
                    <th style={{ width: 50 }}>Text</th>
                    <th style={{ width: 70 }}>Scanned</th>
                    <th style={{ width: 80 }}>Matched by</th>
                    <th style={{ width: 80 }}>Confidence</th>
                    <th style={{ width: 90 }}>Extracted</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((match) => {
                    const selected = match.section
                      ? {
                          value: match.section.key,
                          label: `${match.section.code} — ${match.section.title}`,
                        }
                      : null;
                    const stats = match.file.pageStats;
                    return (
                      <tr key={match.file.fileName}>
                        <td title={match.file.fileName}>
                          {baseName(match.file.fileName)}
                          {match.declaredNotApplicable && (
                            <span style={{ color: "#757575" }}>
                              {" "}
                              (declares N/A)
                            </span>
                          )}
                        </td>
                        <td>
                          <Dropdown
                            options={overrideOptions}
                            value={selected}
                            onChange={(option): void => {
                              handleOverride(
                                match.file.fileName,
                                option && typeof option.value === "string"
                                  ? option.value
                                  : null
                              );
                            }}
                            placeholder="Unmatched — select section"
                            isClearable
                          />
                        </td>
                        <td>
                          {match.variant === undefined
                            ? "—"
                            : match.variant || "(default)"}
                        </td>
                        <td>{stats ? stats.totalPages : "—"}</td>
                        <td>{stats ? stats.textPages : "—"}</td>
                        <td
                          style={{
                            color:
                              stats && stats.scannedPages > 0
                                ? "#e09b00"
                                : undefined,
                            fontWeight:
                              stats && stats.scannedPages > 0 ? 600 : undefined,
                          }}
                        >
                          {stats ? stats.scannedPages : "—"}
                        </td>
                        <td>{match.matchedBy}</td>
                        <td
                          style={{
                            color: CONFIDENCE_COLOR[match.confidence] || "#333",
                            fontWeight: 600,
                          }}
                        >
                          {match.confidence}
                        </td>
                        <td>
                          <LinkButton
                            type="button"
                            onClick={(): void => setTranscriptionMatch(match)}
                          >
                            View text
                          </LinkButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </MatrixTable>
            </Card>
          )}

          {/* Card 5: grouped analysis */}
          {matches && (
            <PrintCard ref={printRef}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>
                  Grouped compliance analysis
                </h3>
                <NoPrint
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  <Button
                    label={
                      analysisRunning
                        ? "Running… PLEASE WAIT"
                        : "Run grouped analysis"
                    }
                    onClick={handleRunAnalysis}
                    disabled={analysisRunning || parsing}
                  />
                  <Button
                    label="Prepare printout"
                    onClick={handlePrint}
                    disabled={!groupResults.length}
                  />
                  <Button
                    label="Clear results"
                    onClick={(): void => setGroupResults([])}
                    disabled={!groupResults.length || analysisRunning}
                  />
                </NoPrint>
              </div>
              <p style={{ margin: "0 0 12px", color: "#555", fontSize: 14 }}>
                Matched files are grouped per section group and
                manufacturer/product variant (e.g. all 3.2.S.4.x files of one
                manufacturer) and each group is checked against the matching
                reference document in one prompt.
                {scannedPageMode === "attach-images"
                  ? " Scanned pages are attached as images."
                  : " Scanned pages were transcribed to text during upload."}
              </p>
              {analysisRunning && progressTotal > 0 && (
                <div style={{ padding: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>
                      Running grouped analysis
                    </div>
                    <div style={{ color: "#333" }}>
                      {Math.floor((progressCompleted / progressTotal) * 100)}%
                    </div>
                  </div>
                  <div
                    style={{
                      height: 10,
                      background: "#eee",
                      borderRadius: 6,
                      overflow: "hidden",
                    }}
                    aria-label="Analysis progress"
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${(progressCompleted / progressTotal) * 100}%`,
                        background: styledTheme.newColors.primary.basic,
                        transition: "width 300ms ease",
                      }}
                    />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "#555" }}>
                    {progressCompleted} of {progressTotal} prompts processed
                    {progressLastGroup ? ` • Last: ${progressLastGroup}` : ""}
                  </div>
                </div>
              )}
              {groupResults.length > 0 ? (
                <div>
                  {groupResults.map(
                    ({ group, result, chunkIndex, chunkTotal }) => {
                      const safeHtml = DOMPurify.sanitize(result.text || "", {
                        USE_PROFILES: { html: true },
                      });
                      const resultKey =
                        chunkIndex && chunkTotal
                          ? `${group.id}::part-${chunkIndex}`
                          : group.id;
                      return (
                        <div key={resultKey} style={{ marginBottom: 12 }}>
                          <h1>
                            Group: {group.label}
                            {chunkIndex && chunkTotal
                              ? ` — Part ${chunkIndex}/${chunkTotal}`
                              : ""}
                          </h1>
                          <div style={{ marginBottom: 4 }}>
                            Files in this group:{" "}
                            {group.files.map((m, i) => (
                              <span key={m.file.fileName}>
                                {i > 0 ? ", " : ""}
                                <span title={m.file.fileName}>
                                  {baseName(m.file.fileName)}
                                </span>
                              </span>
                            ))}
                          </div>
                          {group.reference && (
                            <div>
                              Compared to the following reference:{" "}
                              <a
                                href={group.reference.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {group.reference.sourceName}
                              </a>
                            </div>
                          )}
                          {result.inputTruncated && (
                            <div
                              style={{
                                background: "#fff8e1",
                                border: "1px solid #ffe082",
                                borderRadius: 6,
                                padding: "8px 12px",
                                margin: "8px 0",
                                color: "#7a5c00",
                                fontSize: 14,
                              }}
                            >
                              The prompt was shortened because it was too long,
                              so this result may not cover the full document.
                            </div>
                          )}
                          <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
                        </div>
                      );
                    }
                  )}
                </div>
              ) : (
                !analysisRunning && (
                  <div style={{ color: "#666" }}>
                    No analysis results yet. Click &quot;Run grouped
                    analysis&quot; to start.
                  </div>
                )
              )}
            </PrintCard>
          )}

          <ConfirmationModal
            isOpen={isClearModalOpen}
            title="Clear results?"
            message="This will clear the mapping and all analysis results, and re-enable file uploads. Do you want to continue?"
            closeModal={(): void => setIsClearModalOpen(false)}
            acceptModal={(): void => {
              handleClear();
              setIsClearModalOpen(false);
            }}
            yesButtonText="Clear"
          />

          {transcriptionMatch && (
            <TranscriptionModal
              footerContent={transcriptionCloseButton}
              id="TranscriptionPreviewModal"
              title={baseName(transcriptionMatch.file.fileName)}
              onClose={(): void => setTranscriptionMatch(null)}
            >
              <TranscriptionBody>
                <div
                  style={{ fontSize: 13, color: "#555", marginBottom: 8 }}
                  title={transcriptionMatch.file.fileName}
                >
                  {transcriptionMatch.file.fileName}
                </div>
                {transcriptionMatch.file.pageStats && (
                  <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>
                    {transcriptionMatch.file.pageStats.totalPages} pages ·{" "}
                    {transcriptionMatch.file.pageStats.textPages} text ·{" "}
                    {transcriptionMatch.file.pageStats.scannedPages} scanned
                  </div>
                )}
                {(transcriptionMatch.file.text || "").includes(
                  "see attached image"
                ) && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#e09b00",
                      marginBottom: 8,
                    }}
                  >
                    Some pages were attached as images (not transcribed to
                    text).
                  </div>
                )}
                {transcriptionMatch.file.text ? (
                  <TranscriptionPre>
                    {transcriptionMatch.file.text}
                  </TranscriptionPre>
                ) : (
                  <div style={{ color: "#666" }}>No extracted text.</div>
                )}
              </TranscriptionBody>
            </TranscriptionModal>
          )}
        </ContentSection>
      </Container>
    </LayoutDefault>
  );
};

export default EctdMapping;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;

  > div {
    margin-right: 24px;
    margin-bottom: 24px;
  }
`;

const Card = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-top: 24px;
  padding: 16px;
  background: #fafafa;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #fff;
  padding: 4px 12px;
  font-size: 13px;
`;

const ModeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const ModeToggle = styled.button<{ $active: boolean }>`
  border: 1px solid
    ${(p): string => (p.$active ? styledTheme.newColors.primary.basic : "#ccc")};
  background: ${(p): string =>
    p.$active ? styledTheme.newColors.primary.basic : "#fff"};
  color: ${(p): string => (p.$active ? "#fff" : "#333")};
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const VariantHeader = styled.h4`
  margin: 12px 0 0;
  padding: 8px 10px;
  background: #e0e0e0;
  border: 1px solid #e0e0e0;
  border-bottom: none;
  font-size: 14px;
  font-weight: 600;
`;

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;

  th,
  td {
    border: 1px solid #e0e0e0;
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #e8e8e8;
  }

  tr.section-header td {
    background: #f0f0f0;
    font-weight: 600;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  color: #fff;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
`;

const CoveredTick = styled.span`
  color: #1e7d32;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
`;

const OptionalMark = styled.span`
  color: #999;
  margin-left: 2px;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${(): string => styledTheme.newColors.primary.basic};
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  text-decoration: underline;
`;

const NoPrint = styled.div`
  @media print {
    display: none;
  }
`;

const PrintCard = styled(Card)`
  @media print {
    display: block;

    @page {
      margin: 1cm;
    }
  }
`;

const TranscriptionModal = styled(Modal)`
  & > .modal-dialog {
    width: min(900px, 92vw);
  }

  & > .modal-dialog > .modal-content-footer > .modal-content-wrapper {
    padding: 1rem 1.5rem;
    max-height: 80vh;
  }

  &
    > .modal-dialog
    > .modal-content-footer
    > .modal-content-wrapper
    > .modal-content {
    min-width: 0;
    width: 100%;
    padding: 0;
  }
`;

const TranscriptionBody = styled.div`
  width: 100%;
`;

const TranscriptionPre = styled.pre`
  margin: 0;
  padding: 12px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  max-height: 70vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.45;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
`;
